import { useEffect, useState, useMemo } from "react";
import { subscribeOrders, deleteAllOrders } from "../../../services/orders.service";
import { FiCalendar, FiClock, FiCheck, FiPackage, FiDollarSign, FiRotateCcw } from "react-icons/fi";

const getDateKey = (timestamp) => {
  if (!timestamp?.toMillis) return "";
  const d = new Date(timestamp.toMillis());
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};

const OrdersStatsPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const unsub = subscribeOrders((data) => {
      setOrders(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const stats = useMemo(() => {
    const todayKey = (() => {
      const d = new Date();
      return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
    })();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey =
      yesterday.getFullYear() +
      "-" +
      String(yesterday.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(yesterday.getDate()).padStart(2, "0");

    let todayCount = 0;
    let yesterdayCount = 0;
    let pendingCount = 0;
    let confirmedCount = 0;
    let perfunduarCount = 0;
    let revenueTodayPerfunduar = 0;

    orders.forEach((o) => {
      const key = getDateKey(o.createdAt);
      if (key === todayKey) todayCount++;
      if (key === yesterdayKey) yesterdayCount++;
      const s = o.status || "pending";
      if (s === "pending") pendingCount++;
      else if (s === "confirmed") confirmedCount++;
      else perfunduarCount++;
      if (key === todayKey && s === "perfunduar") {
        revenueTodayPerfunduar += Number(o.total) || 0;
      }
    });

    return {
      todayCount,
      yesterdayCount,
      pendingCount,
      confirmedCount,
      perfunduarCount,
      total: orders.length,
      revenueTodayPerfunduar,
    };
  }, [orders]);

  const handleRiktheNgaFillimi = () => {
    if (
      !window.confirm(
        "A jeni të sigurt? Kjo do të fshijë TË GJITHA porosit. Ky veprim nuk mund të anulohet."
      )
    )
      return;
    setResetting(true);
    deleteAllOrders()
      .then(() => setResetting(false))
      .catch((err) => {
        console.error(err);
        setResetting(false);
        alert("Gabim gjatë fshirjes.");
      });
  };

  if (loading) {
    return (
      <div className="min-h-[200px] flex flex-col justify-center">
        <h2 className="mb-2 text-2xl font-bold text-[#000000]">Përmbledhje porosish</h2>
        <p className="text-[#a6a6a6]">Duke ngarkuar...</p>
      </div>
    );
  }

  const cards = [
    {
      title: "Porosi sot",
      value: stats.todayCount,
      icon: FiCalendar,
      className: "border-[#e0d8c3]/50 bg-[#e0d8c3]/20",
      iconBg: "bg-[#e0d8c3]/40 text-[#000000]",
    },
    {
      title: "Porosi dje",
      value: stats.yesterdayCount,
      icon: FiClock,
      className: "border-[#a6a6a6]/40 bg-[#a6a6a6]/10",
      iconBg: "bg-[#a6a6a6]/20 text-[#000000]",
    },
    {
      title: "Në pritje",
      value: stats.pendingCount,
      icon: FiClock,
      className: "border-[#a6a6a6]/40 bg-[#e0d8c3]/15",
      iconBg: "bg-[#a6a6a6]/25 text-[#000000]",
    },
    {
      title: "E konfirmuar",
      value: stats.confirmedCount,
      icon: FiCheck,
      className: "border-[#e0d8c3] bg-[#e0d8c3]/25",
      iconBg: "bg-[#e0d8c3]/50 text-[#000000]",
    },
    {
      title: "Perfunduar",
      value: stats.perfunduarCount,
      icon: FiPackage,
      className: "border-[#a6a6a6]/40 bg-[#a6a6a6]/10",
      iconBg: "bg-[#a6a6a6]/20 text-[#000000]",
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#000000]">Përmbledhje porosish</h2>
          <p className="mt-1 text-sm text-[#a6a6a6]">
            Statistika e porosive sipas datës dhe statusit.
          </p>
        </div>
      </div>

      {/* Revenue today (confirmed) - hero card */}
      <div className="rounded-2xl border-2 border-[#e0d8c3] bg-[#e0d8c3]/20 p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#000000] text-[#e0d8c3]">
            <FiDollarSign size={28} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#a6a6a6]">
              Fitimi sot (porosi të perfunduara)
            </p>
            <p className="mt-1 text-3xl font-extrabold text-[#000000] sm:text-4xl">
              €{stats.revenueTodayPerfunduar.toFixed(2)}
            </p>
            <p className="mt-1 text-sm text-[#a6a6a6]">
              Shuma e porosive të perfunduara (arritur) që u mbyllën sot.
            </p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className={`rounded-2xl border p-5 shadow-sm transition hover:shadow-md ${card.className}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#a6a6a6]">
                    {card.title}
                  </p>
                  <p className="mt-2 text-2xl font-bold text-[#000000]">{card.value}</p>
                </div>
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${card.iconBg}`}
                >
                  <Icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total + Reset */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch sm:justify-between">
        <div className="flex-1 rounded-2xl border border-[#a6a6a6]/30 bg-white p-5 shadow-sm">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#a6a6a6]">
            Total porosi (të gjitha kohërat)
          </h3>
          <p className="mt-2 text-3xl font-bold text-[#000000]">{stats.total}</p>
        </div>
        <div className="flex items-center">
          <button
            type="button"
            onClick={handleRiktheNgaFillimi}
            disabled={resetting || orders.length === 0}
            className="inline-flex items-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800 transition hover:border-red-300 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiRotateCcw size={18} className={resetting ? "animate-spin" : ""} />
            {resetting ? "Duke fshirë..." : "Rikthe nga fillimi"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrdersStatsPage;
